import React, { useMemo } from 'react';
import { TodoSection } from './TodoSection';
import { SectionTodoEntry } from './SectionTodoEntry';
import { TodoItem } from '../types/todo';
import { SortType } from './SortDropdown';
import SortDropdownNew from './SortDropdownNew';
import { CompletedToggle, CompletedFilter } from './CompletedToggle';
import './TodoSectionWrapper.css';

interface TodoSectionWrapperProps {
  groupedTodos: Record<string, TodoItem[]>;
  filters: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string };
  setEditingTodo: (todo: TodoItem) => void;
  highlightedTaskId?: number | null;
  highlightMessage?: string;
  onTodoUpdate?: (taskId: number, message?: string) => void;
  sortType: SortType;
  setSortType: (sortType: SortType) => void;
  lastDroppedId?: number | null;
  onTaskInteraction?: () => void;
  totalCompletedCount: number;
  completedFilter: CompletedFilter;
  onCompletedFilterChange: (filter: CompletedFilter) => void;
  onNewTodoCreated?: (taskId: number, title: string) => void;
  onFiltersChange?: (newFilters: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }) => void;
  activeDragId?: string | null;
  dragOverInfo?: { sectionId: string; index: number } | null;
  enableDnd?: boolean;
}

// This wrapper is responsible for rendering all TodoSection instances
// It receives all props from TodoList and delegates section-specific logic to TodoSection
export const TodoSectionWrapper = ({
  groupedTodos,
  filters,
  setEditingTodo,
  highlightedTaskId,
  highlightMessage,
  onTodoUpdate,
  sortType,
  setSortType,
  lastDroppedId,
  onTaskInteraction,
  totalCompletedCount,
  completedFilter,
  onCompletedFilterChange,
  onNewTodoCreated,
  onFiltersChange,
  activeDragId,
  dragOverInfo,
  enableDnd = true
}: TodoSectionWrapperProps) => {
  // Section definitions
  // Inline Lucide icons to avoid import
  const LucideCalendarX = (props: React.SVGProps<SVGSVGElement>) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><line x1="16" x2="8" y1="9" y2="17"/><line x1="8" x2="16" y1="9" y2="17"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/></svg>
  );
  const LucideCalendar = (props: React.SVGProps<SVGSVGElement>) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
  );
  const LucideCalendarDays = (props: React.SVGProps<SVGSVGElement>) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/></svg>
  );
  const LucideClock = (props: React.SVGProps<SVGSVGElement>) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
  );
  const LucideCheckCircle = (props: React.SVGProps<SVGSVGElement>) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg>
  );
  // Palm tree emoji for weekend section - much more recognizable!
  const PalmTreeEmoji = () => (
    <div style={{ fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🌴</div>
  );
  const sections = useMemo(() => [
    {
      title: 'OVERDUE',
      Icon: LucideCalendarX,
      todos: groupedTodos.overdue,
      sectionClass: 'overdue-section',
      droppableId: 'overdue',
    },
    {
      title: 'TODAY',
      Icon: LucideCalendar,
      todos: groupedTodos.today,
      sectionClass: 'today-section',
      droppableId: 'today',
    },
    {
      title: 'TOMORROW',
      Icon: LucideCalendarDays,
      todos: groupedTodos.tomorrow,
      sectionClass: 'tomorrow-section',
      droppableId: 'tomorrow',
    },
    {
      title: 'THIS WEEKEND',
      Icon: PalmTreeEmoji,
      todos: groupedTodos.thisWeekend,
      sectionClass: 'weekend-section',
      droppableId: 'thisWeekend',
    },
    {
      title: 'LATER',
      Icon: LucideClock,
      todos: groupedTodos.later,
      sectionClass: 'later-section',
      droppableId: 'later',
    },
    {
      title: 'NO DUE DATE',
      Icon: null,
      todos: groupedTodos.noDueDate,
      sectionClass: 'no-date-section',
      droppableId: 'noDueDate',
    },
    // Add completed section at the end
    {
      title: 'COMPLETED',
      Icon: LucideCheckCircle,
      todos: groupedTodos.completed,
      sectionClass: 'completed-section',
      droppableId: 'completed',
    },
  ], [
    groupedTodos.overdue,
    groupedTodos.today,
    groupedTodos.tomorrow,
    groupedTodos.thisWeekend,
    groupedTodos.later,
    groupedTodos.noDueDate,
    groupedTodos.completed
  ]);

  // Calculate total number of todos to determine if sort should be shown
  const totalTodos = sections.reduce((sum, section) => {
    return sum + (Array.isArray(section.todos) ? section.todos.length : 0);
  }, 0);

  // Calculate if only completed tasks are showing (all other sections are empty)
  const activeSections = sections.filter(section => section.title !== 'COMPLETED');
  const totalActiveTodos = activeSections.reduce((sum, section) => {
    return sum + (Array.isArray(section.todos) ? section.todos.length : 0);
  }, 0);
  const onlyCompletedShowing = totalActiveTodos === 0 && totalCompletedCount > 0;

  return (
    <div style={{ position: 'relative' }}>
      {/* Sort component positioned at rightmost edge of screen */}
      {totalTodos > 0 && (
        <div className="section-header-sort-container">
          <SortDropdownNew value={sortType} onChange={setSortType} compact={true} />
        </div>
      )}
      
      {/* Quick add entry when only completed tasks are showing */}
      {onlyCompletedShowing && (
        <div className="quick-add-section">
          <SectionTodoEntry
            sectionType="noDueDate"
            currentFilters={filters}
            onNewTodoCreated={onNewTodoCreated}
            onFiltersChange={onFiltersChange}
          />
        </div>
      )}
      
      {sections.map(section => {
        const count = Array.isArray(section.todos) ? section.todos.length : 0;
        const isCompleted = section.title === 'COMPLETED';
        
        // Always show completed section if there are completed todos, even if no active todos
        if (count === 0 && !isCompleted) return null;
        if (count === 0 && isCompleted && totalCompletedCount === 0) return null; // Don't show empty completed section when there are no completed todos at all
        
        // Determine if we're dragging an item from a different section
        const isDraggingFromOtherSection = !!activeDragId && !section.todos.some(todo => String(todo.id) === activeDragId);
        
        // Get drop target message for cross-section drops
        const getDropTargetMessage = (): string => {
          switch (section.title) {
            case 'TODAY':
              return '📅 Drop your task here to schedule it for today!';
            case 'TOMORROW':
              return '📅 Drop your task here to schedule it for tomorrow!';
            case 'THIS WEEKEND':
              return '🏖️ Drop your task here to plan it for this weekend!';
            case 'LATER':
              return '📆 Drop your task here to schedule it for later!';
            case 'OVERDUE':
              return '⚠️ Drop your task here to mark it as overdue!';
            case 'NO DUE DATE':
              return '📝 Drop your task here to remove its due date!';
            case 'COMPLETED':
              return '✅ Drop your task here to mark it as completed!';
            default:
              return 'Drop your task here!';
          }
        };
        
        // Icon color by section
        let iconColor = '#888';
        switch (section.title) {
          case 'OVERDUE': iconColor = '#e57373'; break; // subtle red
          case 'TODAY': iconColor = '#42a5f5'; break; // subtle blue
          case 'TOMORROW': iconColor = '#ffd54f'; break; // subtle yellow
          case 'THIS WEEKEND': iconColor = '#4ade80'; break; // cheerful green for weekend
          case 'LATER': iconColor = '#ab47bc'; break; // subtle purple
          case 'COMPLETED': iconColor = '#43d675'; break; // subtle green
          default: iconColor = '#888';
        }
        
        // Always show header for COMPLETED section and all other sections with todos
        const showHeader = true;
        
        return (
          <div key={section.title} className={section.sectionClass}>
            {showHeader && (
              <div className="section-header">
                <div className="section-header-left">
                  {section.Icon && (
                    <section.Icon style={{ color: iconColor }} />
                  )}
                  <span className="section-header-title">{section.title}</span>
                  <span className="section-header-count">({isCompleted ? `${count}/${totalCompletedCount}` : count})</span>
                </div>
                {isCompleted && (
                  <div className="section-header-toggle">
                    <CompletedToggle 
                      value={completedFilter} 
                      onChange={onCompletedFilterChange}
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* TodoSection wrapped with drop target overlay */}
            <div style={{ position: 'relative' }}>
              {/* Simple drop target overlay - only covers section content, not header */}
              {isDraggingFromOtherSection && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    border: '2px dashed rgba(59, 130, 246, 0.4)',
                    borderRadius: '12px',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      textAlign: 'center',
                      color: '#3b82f6',
                      fontSize: '16px',
                      fontWeight: '600',
                      maxWidth: '300px',
                      lineHeight: '1.5',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    {getDropTargetMessage()}
                  </div>
                </div>
              )}
              
              <TodoSection
              title={section.title}
              Icon={null}
              todos={section.todos}
              sectionClass={section.sectionClass}
              droppableId={section.droppableId}
              showListFilter={true}
              filtersList={filters.list}
              setEditingTodo={setEditingTodo}
              highlightedTaskId={highlightedTaskId}
              highlightMessage={highlightMessage}
              onTodoUpdate={onTodoUpdate}
              sortType={sortType}
              lastDroppedId={lastDroppedId}
              onTaskInteraction={onTaskInteraction}
              currentFilters={filters}
              onNewTodoCreated={onNewTodoCreated}
              onFiltersChange={onFiltersChange}
              activeDragId={activeDragId}
              dragOverInfo={dragOverInfo}
              enableDnd={enableDnd}
            />
            </div>
          </div>
        );
      })}
    </div>
  );
};
